import { useState } from 'react';
import { useSalesAnalytics, useChannelAnalytics, useBrandAnalytics } from '../hooks/useData';
import { formatCurrency, formatNumber } from '../utils/api';

function SimpleBarChart({ data, dataKey, label, color = 'blue' }) {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => d[dataKey]));

  return (
    <div className="space-y-2">
      {data.slice(0, 8).map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-20 text-xs text-gray-500 truncate">
            {item.date || item.channel || item.brand}
          </div>
          <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
            <div
              className={`h-full bg-${color}-500`}
              style={{
                width: `${(item[dataKey] / maxValue) * 100}%`,
                backgroundColor: color === 'blue' ? '#3b82f6' : color === 'purple' ? '#8b5cf6' : '#10b981'
              }}
            />
          </div>
          <div className="w-24 text-xs text-gray-700 text-right">
            {dataKey === 'revenue' ? formatCurrency(item[dataKey]) : formatNumber(item[dataKey])}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Analytics() {
  const [period, setPeriod] = useState('weekly');
  const { data: salesData, isLoading: salesLoading } = useSalesAnalytics(period);
  const { data: channelData, isLoading: channelLoading } = useChannelAnalytics();
  const { data: brandData, isLoading: brandLoading } = useBrandAnalytics();

  const isLoading = salesLoading || channelLoading || brandLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate totals
  const totalRevenue = channelData?.reduce((sum, c) => sum + c.revenue, 0) || 0;
  const totalUnits = channelData?.reduce((sum, c) => sum + c.units, 0) || 0;
  const totalOrders = channelData?.reduce((sum, c) => sum + c.orders, 0) || 0;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500">Sales performance and trends</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPeriod('weekly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              period === 'weekly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Weekly
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              period === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Units Sold</p>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(totalUnits)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Total Orders</p>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(totalOrders)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Avg Order Value</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(avgOrderValue)}</p>
        </div>
      </div>

      {/* Sales Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales Trend</h2>
        <SimpleBarChart data={salesData?.slice(-10)} dataKey="revenue" label="Revenue" color="blue" />
      </div>

      {/* Channel & Brand Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Channel</h2>
          <SimpleBarChart data={channelData} dataKey="revenue" label="Revenue" color="blue" />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Channel Distribution</h2>
          <div className="space-y-3">
            {channelData?.map((channel, index) => (
              <div key={channel.channel} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981'][index % 4]
                    }}
                  />
                  <span className="text-gray-700">{channel.channel}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500">{formatCurrency(channel.revenue)}</span>
                  <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm font-medium">
                    {channel.revenue_share}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Brand Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Brands by Revenue</h2>
        <SimpleBarChart data={brandData?.slice(0, 10)} dataKey="revenue" label="Revenue" color="purple" />
      </div>

      {/* Channel Details Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Channel Performance</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Channel</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Units</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Orders</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">AOV</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Share</th>
              </tr>
            </thead>
            <tbody>
              {channelData?.map((channel) => (
                <tr key={channel.channel} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{channel.channel}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(channel.revenue)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatNumber(channel.units)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatNumber(channel.orders)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">
                    {formatCurrency(channel.orders > 0 ? channel.revenue / channel.orders : 0)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm font-medium">
                      {channel.revenue_share}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Brand Details Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Brand Performance</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Brand</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Units</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Avg Price</th>
              </tr>
            </thead>
            <tbody>
              {brandData?.slice(0, 15).map((brand) => (
                <tr key={brand.brand} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{brand.brand}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(brand.revenue)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{formatNumber(brand.units)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">
                    {formatCurrency(brand.units > 0 ? brand.revenue / brand.units : 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
